import { FaRegHeart, FaHeart } from "react-icons/fa";
import "./CouponCard.css";
import { useState } from "react";
import BlockClubLogo from "../../media/BC.png";

export function CouponCard({
  openConfirm,
  coupon,
  title,
  description,
  expiration,
  frequency,
  redeemed,
  isSaving,
  isSaved,
  isReady,
  handleWishlistToggle,
  disabled,
  requiresLogin = true,
  venueName = null,
  onVenueNameClick = null,
  venueLogo = null,
}) {

  const [isSavedAsFavorite, setIsSavedAsFavorite] = useState(false);

  // Determine logo source - use BC logo directly if venue has no logo
  const hasVenueLogo = Boolean(venueLogo && venueLogo !== BlockClubLogo);
  const logoSrc = hasVenueLogo ? venueLogo : BlockClubLogo;

  const [logoError, setLogoError] = useState(false);
  const [fallbackSrc, setFallbackSrc] = useState(null);

  // Format frequency text
  const getFrequencyText = () => {
    if (!frequency) return null;
    if (frequency === 'always_on') return 'Reusable';
    if (frequency === 'Limited' || frequency === 'one_time') {
      return `Valid for a ${frequency.toLowerCase()} time`;
    }
    return `Valid ${frequency.toLowerCase()}`;
  };

  return (
    <div className={`coupon-card ${redeemed ? 'coupon-redeemed' : ''}`}>
      {/* Redeemed Watermark */}
      {redeemed && (
        <div className="redeemed-watermark">REDEEMED</div>
      )}

      <div className="coupon-card-body">
        {/* Venue Logo - on the left side (only in wishlist context) */}
        {venueLogo && (
          <div className="coupon-venue-logo-container">
            <img
              src={fallbackSrc || logoSrc}
              alt={venueName || "Venue"}
              className="coupon-venue-logo"
              onError={(e) => {
                // Only trigger fallback if venue has a logo that failed to load
                if (!logoError && hasVenueLogo && !fallbackSrc) {
                  setLogoError(true);
                  setFallbackSrc(BlockClubLogo);
                }
              }}
            />
          </div>
        )}

        {/* Image - if available */}
        {coupon.image_url && (
          <div className="coupon-image-container">
            <img
              src={coupon.image_url}
              alt={title}
              className="coupon-image"
            />
          </div>
        )}

        {/* Content */}
        <div className="coupon-content-wrapper">
          <div className="coupon-info-section">
            <h3 className="coupon-title">{title}</h3>

            {/* Venue Name - Only show in wishlist context */}
            {venueName && (
              <button
                className="coupon-venue-name"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onVenueNameClick) onVenueNameClick();
                }}
                title={`View all coupons at ${venueName}`}
              >
                @ {venueName}
              </button>
            )}

            <p className="coupon-description">{description}</p>
            <p className="coupon-expiration">
              {expiration || 'No Expiration'}
            </p>
          </div>

          {/* Frequency Badge */}
          {frequency && (
            <div className="coupon-frequency-wrapper">
              <span className="coupon-frequency">
                {getFrequencyText()}
              </span>
            </div>
          )}
        </div>

        {/* Save Button - Floating */}
        {isReady && (
          <button
            className={`wishlist-heart-btn ${isSaved ? "saved" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              handleWishlistToggle(coupon);
              setIsSavedAsFavorite(prev => !prev);
            }}
            disabled={isSaving}
            title={isSaved ? "Remove from wishlist" : "Save to wishlist"}
            aria-label={isSaved ? "Remove from wishlist" : "Save to wishlist"}
          >
            {isSaved ? (
              <FaHeart className="heart-icon saved" />
            ) : (
              <FaRegHeart className="heart-icon" />
            )}
          </button>
        )}
      </div>

      {/* Redeem Button - Only show if not redeemed */}
      {!redeemed && (
        <div className="coupon-button-container">
          <button
            className="redeem-button"
            onClick={() => openConfirm(coupon)}
            disabled={disabled}
            aria-label={requiresLogin ? "Login to redeem this coupon" : "Redeem this coupon"}
          >
            {requiresLogin ? "Login to Redeem" : "Redeem"}
          </button>
        </div>
      )}

      {/* Redeemed Date - Show if redeemed */}
      {redeemed && (
        <div className="redeemed-date-container">
          <p className="redeemed-date-text">
            Redeemed on {new Date(coupon.redeemed_at).toLocaleDateString()}
          </p>
        </div>
      )}
    </div>
  );
}
